import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { GeneratorTab } from './etichete/GeneratorTab'
import { PreviewTab } from './etichete/PreviewTab'
import { RegistruTab } from './etichete/RegistruTab'
import { EfsaTab } from './etichete/EfsaTab'
import type {
  Eticheta,
  EfsaIngredient,
  LabelStandard,
  EticheteStatus,
  IngredientItem,
  BeneficiuItem,
} from './etichete/types'

type Tab = 'generator' | 'preview' | 'registru' | 'efsa'

const TAB_LABELS: Record<Tab, string> = {
  generator: 'Generator',
  preview: 'Previzualizare și export',
  registru: 'Registru',
  efsa: 'EFSA 2025',
}

const TAB_COLORS: Record<Tab, string> = {
  generator: 'border-b-b4l-green text-b4l-green',
  preview: 'border-b-b4l-blue text-b4l-blue',
  registru: 'border-b-b4l-purple text-b4l-purple',
  efsa: 'border-b-b4l-text-dim text-b4l-text-dim',
}

const EMPTY_DRAFT: Partial<Eticheta> = {
  tip: 'tip1',
  denumire: '',
  pictograme: [],
  ingrediente: [],
  beneficii: [],
  hasRosu: false,
}

export function GeneratorEtichetePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.label:write')

  const [tab, setTab] = useState<Tab>('generator')
  const [draft, setDraft] = useState<Partial<Eticheta>>(EMPTY_DRAFT)
  const [editingId, setEditingId] = useState<number | null>(null)

  const ingredienteQuery = useQuery<{ ingrediente: EfsaIngredient[] }>({
    queryKey: ['etichete-efsa'],
    queryFn: async () =>
      (await api.get<{ ingrediente: EfsaIngredient[] }>('/marketing/etichete/efsa')).data,
    staleTime: 5 * 60_000,
  })

  const standardsQuery = useQuery<{ standards: LabelStandard[] }>({
    queryKey: ['etichete-standards'],
    queryFn: async () =>
      (await api.get<{ standards: LabelStandard[] }>('/marketing/etichete/standards')).data,
    staleTime: 5 * 60_000,
  })

  const etichetelQuery = useQuery<{ etichete: Eticheta[] }>({
    queryKey: ['etichete'],
    queryFn: async () => (await api.get<{ etichete: Eticheta[] }>('/marketing/etichete')).data,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = serializeDraft(draft)
      if (editingId) {
        return (await api.patch(`/marketing/etichete/${editingId}`, payload)).data
      }
      return (await api.post('/marketing/etichete', payload)).data
    },
    onSuccess: (data: { eticheta: Eticheta }) => {
      toast.success(
        editingId ? 'Eticheta a fost salvată' : `Eticheta ${data.eticheta.idIntern} a fost creată`,
      )
      queryClient.invalidateQueries({ queryKey: ['etichete'] })
      setEditingId(data.eticheta.id)
      setDraft(eticheteToDraft(data.eticheta))
      setTab('preview')
    },
    onError: showError,
  })

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: EticheteStatus }) =>
      (await api.patch(`/marketing/etichete/${id}`, { status })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['etichete'] })
      toast.success('Stare actualizată')
    },
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/marketing/etichete/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['etichete'] })
      toast.success('Eticheta a fost ștearsă')
    },
    onError: showError,
  })

  const exportMutation = useMutation({
    mutationFn: async () => {
      if (!editingId) throw new Error('Nu există o etichetă salvată')
      const resp = await api.get(`/marketing/etichete/${editingId}/export-txt`, {
        responseType: 'blob',
      })
      const blob = resp.data as Blob
      const cd = (resp.headers['content-disposition'] ?? '') as string
      const filename = cd.match(/filename="(.+?)"/)?.[1] ?? `eticheta_${editingId}.txt`
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },
    onSuccess: () => toast.success('Fișierul a fost descărcat'),
    onError: () => toast.error('Eroare la export'),
  })

  const handleNew = () => {
    setDraft(EMPTY_DRAFT)
    setEditingId(null)
    setTab('generator')
  }

  const handleEdit = (e: Eticheta) => {
    setDraft(eticheteToDraft(e))
    setEditingId(e.id)
    setTab('generator')
  }

  const handlePreview = (e: Eticheta) => {
    setDraft(eticheteToDraft(e))
    setEditingId(e.id)
    setTab('preview')
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Marketing → Generator etichete</h1>
        {editingId && (
          <span className="text-xs text-b4l-text-muted">
            Editare:{' '}
            <span className="font-mono text-b4l-purple-dark">
              {(draft as Eticheta).idIntern ?? `#${editingId}`}
            </span>
            {' · '}
            <button onClick={handleNew} className="text-b4l-blue-dark hover:underline">
              Etichetă nouă
            </button>
          </span>
        )}
      </div>

      <div className="border-b border-b4l-border">
        <div className="flex gap-1">
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`border-b-2 px-4 py-2 text-sm transition ${
                tab === t
                  ? `${TAB_COLORS[t]} font-semibold`
                  : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {tab === 'generator' && (
        <GeneratorTab
          draft={draft}
          setDraft={setDraft}
          ingrediente={ingredienteQuery.data?.ingrediente ?? []}
          onSave={() => saveMutation.mutate()}
          saving={saveMutation.isPending}
          isEdit={!!editingId}
          onPreview={() => setTab('preview')}
        />
      )}

      {tab === 'preview' && (
        <PreviewTab
          draft={draft}
          standards={standardsQuery.data?.standards ?? []}
          onExportTxt={() => exportMutation.mutate()}
          exportLoading={exportMutation.isPending}
          onBackToGenerator={() => setTab('generator')}
          isPersisted={!!editingId}
        />
      )}

      {tab === 'registru' && (
        <RegistruTab
          etichete={etichetelQuery.data?.etichete ?? []}
          canWrite={canWrite}
          onEdit={handleEdit}
          onPreview={handlePreview}
          onUpdateStatus={(id, status) => updateStatusMutation.mutate({ id, status })}
          onDelete={(id) => deleteMutation.mutate(id)}
          onNew={handleNew}
        />
      )}

      {tab === 'efsa' && <EfsaTab ingrediente={ingredienteQuery.data?.ingrediente ?? []} />}
    </div>
  )
}

function serializeDraft(d: Partial<Eticheta>): Record<string, unknown> {
  return {
    tip: d.tip ?? 'tip1',
    denumire: d.denumire ?? '',
    subtitlu: d.subtitlu ?? null,
    portii: d.portii ?? null,
    forma: d.forma ?? null,
    gramaj: d.gramaj ?? null,
    madeIn: d.madeIn ?? null,
    ingrediente: (d.ingrediente as IngredientItem[]) ?? [],
    mentiune: d.mentiune ?? null,
    beneficii: (d.beneficii as BeneficiuItem[]) ?? [],
    compozitie: d.compozitie ?? null,
    utilizareAdulti: d.utilizareAdulti ?? null,
    utilizareCopii: d.utilizareCopii ?? null,
    tara: d.tara ?? null,
    adresa: d.adresa ?? null,
    autoritate: d.autoritate ?? null,
    notificare: d.notificare ?? null,
    detinator: d.detinator ?? null,
    ean: d.ean ?? null,
    avertSpecific: d.avertSpecific ?? null,
    pictograme: d.pictograme ?? [],
    dimW: d.dimW ?? null,
    dimH: d.dimH ?? null,
    hasRosu: !!(d.beneficii as BeneficiuItem[])?.some((b) => b.status === 'rosu'),
  }
}

function eticheteToDraft(e: Eticheta): Partial<Eticheta> {
  return {
    ...e,
    ingrediente: (e.ingrediente as IngredientItem[]) ?? [],
    beneficii: (e.beneficii as BeneficiuItem[]) ?? [],
    pictograme: e.pictograme ?? [],
  }
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
